import { type KeyboardEvent, useRef } from 'react';

import { Container } from '@/components/legacy-ui';
import {
  GUARD_INTELLIGENCE_TIERS,
  intelligenceMonthlyBudget,
  type IntelligenceTierId,
} from '@/sections/settings/subscription/constant';
import { formatPrice } from '@/sections/settings/subscription/util';
import { cn } from '@/utils/classname';

export interface IntelligenceTierSelectorProps {
  selectedTier: IntelligenceTierId;
  onTierChange: (tier: IntelligenceTierId) => void;
  // When true, the entire selector is non-interactive (e.g. during wizard
  // submission). Defaults to false; the configure modal omits it.
  disabled?: boolean;
  // When true, the `none` tier is disabled/unselectable and a minimum-tier
  // explanatory message is shown. Defaults to false.
  requireMinimumTier?: boolean;
  title?: string;
  className?: string;
}

/**
 * IntelligenceTierSelector - shared Guard Intelligence tier picker.
 *
 * The single source of truth for the Intelligence radio-card grid, used by both
 * the configure modal (Settings) and the Add Customer wizard. Mirrors the prop
 * shape of the sibling SubscriptionTierSelector. Unifies the two previous grids:
 * the keyboard arrow-nav + roving tabIndex + "SELECTED" badge (a11y win) from
 * the configure section, and the `disabled` handling from the wizard — the
 * whole-selector disabled state is exposed to assistive tech via aria-disabled.
 * Arrow-key navigation moves DOM focus to the newly selected card (focus follows
 * selection, per the WAI-ARIA radio-group pattern) so screen readers announce the
 * new tier and a subsequent Enter/Space acts on it instead of reverting.
 * Prices render in whole dollars via formatPrice.
 */
export function IntelligenceTierSelector({
  selectedTier,
  onTierChange,
  disabled = false,
  requireMinimumTier = false,
  title = 'Guard Intelligence',
  className,
}: IntelligenceTierSelectorProps) {
  // Roving-tabindex anchor. Normally the selected tier is the single tabbable
  // card; but when the selected tier is the disabled `none` at the platform
  // floor, fall back to the first enabled tier so the radiogroup always keeps a
  // keyboard-reachable entry point (otherwise every card gets tabIndex -1 and
  // keyboard/AT users are trapped — unable to reach Centurion to satisfy the
  // minimum-tier requirement while the Next button is also disabled).
  const selectedTierDisabled = requireMinimumTier && selectedTier === 'none';
  const tabbableTierId = selectedTierDisabled
    ? (GUARD_INTELLIGENCE_TIERS.find(
        t => !(requireMinimumTier && t.id === 'none')
      )?.id ?? selectedTier)
    : selectedTier;

  // Single ref on the radiogroup so arrow-key navigation can move DOM focus to
  // the newly selected card via a data-testid lookup. A single group ref (rather
  // than a per-card ref map) keeps the focus move working through the legacy-ui
  // Container regardless of how it forwards refs. Focus is moved imperatively in
  // the arrow-key branches only — never via an effect keyed on selectedTier —
  // so programmatic/parent-driven selection changes stay focus-neutral.
  const groupRef = useRef<HTMLDivElement | null>(null);
  const focusTier = (id: IntelligenceTierId) => {
    groupRef.current
      ?.querySelector<HTMLElement>(`[data-testid="intelligence-tier-${id}"]`)
      ?.focus();
  };

  return (
    <section
      className={cn('space-y-2', className)}
      aria-labelledby="intelligence-tier-section-title"
    >
      <h3
        id="intelligence-tier-section-title"
        className="text-chariot-text-primary text-sm font-semibold"
      >
        {title}
      </h3>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label="Guard Intelligence tier"
        aria-disabled={disabled || undefined}
        className="grid grid-cols-2 gap-3 sm:grid-cols-3"
      >
        {GUARD_INTELLIGENCE_TIERS.map(tier => {
          const isSelected = selectedTier === tier.id;
          const isDisabled = requireMinimumTier && tier.id === 'none';
          return (
            <Container
              key={tier.id}
              onClick={() => {
                if (isDisabled || disabled) return;
                onTierChange(tier.id as IntelligenceTierId);
              }}
              className={cn(
                'relative cursor-pointer py-3 text-center',
                isDisabled && 'pointer-events-none opacity-40',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              isSelected={isSelected}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={disabled || isDisabled || undefined}
              tabIndex={
                disabled || isDisabled
                  ? -1
                  : tier.id === tabbableTierId
                    ? 0
                    : -1
              }
              onKeyDown={(e: KeyboardEvent) => {
                if (disabled) return;
                if (isDisabled) return;
                const tiers = GUARD_INTELLIGENCE_TIERS;
                // Skip any disabled `none` card when navigating with arrows.
                const isTierDisabled = (idx: number) =>
                  requireMinimumTier && tiers[idx].id === 'none';
                const currentIndex = tiers.findIndex(
                  t => t.id === selectedTier
                );
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  let nextIndex = (currentIndex + 1) % tiers.length;
                  while (isTierDisabled(nextIndex)) {
                    nextIndex = (nextIndex + 1) % tiers.length;
                  }
                  const nextId = tiers[nextIndex].id as IntelligenceTierId;
                  onTierChange(nextId);
                  focusTier(nextId);
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  let prevIndex =
                    (currentIndex - 1 + tiers.length) % tiers.length;
                  while (isTierDisabled(prevIndex)) {
                    prevIndex = (prevIndex - 1 + tiers.length) % tiers.length;
                  }
                  const prevId = tiers[prevIndex].id as IntelligenceTierId;
                  onTierChange(prevId);
                  focusTier(prevId);
                } else if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onTierChange(tier.id as IntelligenceTierId);
                }
              }}
              data-testid={`intelligence-tier-${tier.id}`}
            >
              {isSelected && (
                <div className="bg-chariot-brand-primary absolute -top-2.5 left-1/2 -translate-x-1/2 transform rounded-full px-2 py-0.5 text-[10px] font-bold text-white">
                  SELECTED
                </div>
              )}
              <span className="block text-sm font-medium">{tier.name}</span>
              <span className="text-chariot-brand-primary mt-1 block text-sm font-bold">
                {formatPrice(tier.annualPrice, 'USD')}/yr
              </span>
              {tier.annualPrice > 0 && (
                <span className="text-chariot-text-secondary mt-0.5 block text-xs">
                  ~
                  {formatPrice(
                    intelligenceMonthlyBudget(tier.annualPrice),
                    'USD'
                  )}
                  /mo
                </span>
              )}
            </Container>
          );
        })}
      </div>

      {requireMinimumTier && (
        <p className="text-chariot-text-secondary text-xs">
          Minimum subscriptions must include at least Centurion ($12K in AI
          credits). BYOAI is available above the platform minimum.
        </p>
      )}
    </section>
  );
}
